import React, {useEffect} from 'react';

import {useApi} from "../../providers/ApiProvider";
import {useParams} from "react-router-dom";
import {useNotifications} from "../../providers/NotificationsProvider";
import ProjectsRequests from "../../requests/ProjectsRequests";
import Map from "../map/general/Map";
import {useAuth} from "../../providers/AuthProvider";
import Loading from "../ui/Loading";
import LoadingBig from "../ui/loaders/LoadingBig";
import _ from "lodash";
import UntrackedChanges from "../map/general/ui/UntrackedChanges";

export default function ProjectView({accessToken = false}) {
    const {user, setInactivityActivated} = useAuth();
    const {projectId} = useParams();
    const apiContext = useApi();
    const projectsRequests = new ProjectsRequests(apiContext.api);
    const notifications = useNotifications();

    const [loading, setLoading] = React.useState('');
    const [project, setProject] = React.useState({});
    const projectRef = React.useRef(project);
    const userRef = React.useRef(user);
    const [allowedActions, setAllowedActions] = React.useState({
        edit: false,
        share: false,
    });
    const allowedActionsRef = React.useRef(allowedActions);
    const [untrackedChanges, setUntrackedChanges] = React.useState(false);
    const untrackedChangesRef = React.useRef(null);


    useEffect(() => {
        let viewParams = {
            pk: projectId
        };
        if (accessToken) {
            viewParams.access_token = accessToken;
        }

        projectsRequests.view(viewParams, uploadErrorHandler).then((response) => {
            if (response) {
                setProject(response);
                projectRef.current = response;
                document.title = response.title+' | GeoList';

                setLoading('dataFetched');
            }
        });


        untrackedChangesRef.current = setInterval(trackChanges, 10000);
    }, []);


    useEffect(() => {
        userRef.current = user;
    }, [user]);


    useEffect(() => {
        projectRef.current = project;

        const newCanEdit = userCanEdit();
        const newCanShare = userCanShare();

        if (newCanShare !== allowedActionsRef.current.share || newCanEdit !== allowedActionsRef.current.edit) {
            setAllowedActions({
                edit: newCanEdit,
                share: newCanShare,
            });
            allowedActionsRef.current = {
                edit: newCanEdit,
                share: newCanShare,
            };
        }

        /*if (project && (project.access_level === 'public')) {
            setInactivityActivated(false);
        }*/
    }, [project]);


    const uploadErrorHandler = (response) => {
        if (response && response.data === 'Invalid access token') {
            setLoading('error-invalid-access-token');
        } else {
            setLoading('error');
        }
    }


    const trackChanges = () => {
        let viewParams = {
            pk: projectId
        };
        if (accessToken) {
            viewParams.access_token = accessToken;
        }

        projectsRequests.view(viewParams, uploadErrorHandler).then((response) => {
            let dataEqual = true;

            let fieldsToCompare = ['description', 'title', 'status'];
            fieldsToCompare.forEach((field) => {
                if (response[field] === undefined) {
                    dataEqual = false;
                    //console.log('Field not found in response:', field);
                } else if (projectRef.current[field] === undefined) {
                    dataEqual = false;
                    //console.log('Field not found in project:', field, projectRef.current);
                } else {
                    const eq = _.isEqual(response[field], projectRef.current[field]);
                    if (!eq) {
                        //console.log('Field not equal:', field, response[field], projectRef.current[field]);
                    }

                    dataEqual *= eq;
                }
            });
            if (response.data === undefined || projectRef.current.data === undefined) {
                dataEqual = false;
                //console.log('Data field not found in response or project:', response.data, projectRef.current.data);
            }
            ['shapes', 'marks'].forEach((field) => {
                if (response.data[field] === undefined) {
                    dataEqual = false;
                    //console.log('Field not found in response data:', field);
                } else if (projectRef.current.data[field] === undefined) {
                    dataEqual = false;
                    //console.log('Field not found in project data:', field, projectRef.current.data);
                } else {
                    Object.keys(response.data[field]).forEach((shapeId, index) => {
                        if (response.data[field][shapeId] === undefined) {
                            dataEqual = false;
                            //console.log('Shape field not found in response data:', field, shapeId);
                        } else if (projectRef.current.data[field][shapeId] === undefined) {
                            dataEqual = false;
                            //console.log('Shape field not found in project data:', field, shapeId, projectRef.current.data);
                        } else {
                            const eq = _.isEqual(response.data[field][shapeId].geoJSON, projectRef.current.data[field][shapeId].geoJSON);
                            if (!eq) {
                                //console.log('Shape field not equal:', field, shapeId, response.data[field][shapeId].geoJSON, projectRef.current.data[field][shapeId].geoJSON);
                            }

                            dataEqual *= eq;
                        }
                    });
                }
            });

            let newUserCanEdit = userCanEdit(response),
                newUserCanShare = userCanShare(response);

            //console.log('new edit:', newUserCanEdit);

            if (newUserCanEdit !== allowedActionsRef.current.edit || newUserCanShare !== allowedActionsRef.current.share) {
                dataEqual = false; // If user permissions changed, we consider data as changed
                //console.log('User permissions changed:', allowedActionsRef.current, newUserCanEdit, newUserCanShare);

                setAllowedActions({
                    edit: newUserCanEdit,
                    share: newUserCanShare,
                });
                allowedActionsRef.current = {
                    edit: newUserCanEdit,
                    share: newUserCanShare,
                };
            }

            setUntrackedChanges(!dataEqual);
        });
    }


    const userCanEdit = (newProjectInfo = null) => {
        let projectData = newProjectInfo || projectRef.current;

        if (projectData.status !== '00_in_progress') {
            return false;
        }

        if (projectData && projectData.access_level === 'edit') {
            return true;
        }

        if (!userRef.current || !projectData) {
            return false;
        }

        if (projectData.user_id === userRef.current.id || userRef.current.type === 'admin') {
            return true;
        }

        /*if (project.shares) {
            let share = project.shares.find((share) => share.invited_id === user.id);
            if (share && share.level === 'edit') {
                return true;
            }
        }*/

        return false;
    }


    const userCanShare = (newProjectInfo = null) => {
        let projectData = newProjectInfo || projectRef.current;

        if (!userRef.current || !userRef.current.id || !userRef.current.settings || !userRef.current.settings.sharingAllowed) {
            return false;
        }

        if (userRef.current.type === 'admin') {
            return true;
        }

        return projectData && projectData.user_id === userRef.current.id;
    }


    const onProjectChange = (newProjectData, propagateChanges = true) => {
        let projectUpdatedPromise;
        if (accessToken) {
            projectUpdatedPromise = projectsRequests.updateWithAccessToken(accessToken, projectId, newProjectData);
        } else {
            projectUpdatedPromise = projectsRequests.update(projectId, newProjectData);
        }

        return projectUpdatedPromise.then((response) => {
            if (response.id) {
                projectRef.current = response;

                const projectChanged = _.isEqual(project,response);
                if (projectChanged && propagateChanges) {
                    setProject(response);
                }
            }

            return response;
        })/*.finally(() => {
            trackChanges();
        })*/;
    }


    const onProjectCopy = () => {
        let viewParams = {
            pk: projectId
        };
        if (accessToken) {
            viewParams.access_token = accessToken;
        }

        projectsRequests.copy(viewParams).then((response) => {
            if (response !== false) {
                notifications.notify('Project copied successfully', 'success');
                window.location.href = '/p/'+response.id;
            } else {
                notifications.notify('Project copy failed', 'error');
            }
        });
    }


    const onMapInitialized = () => {
        setLoading('mapInitialized');
    }


    return (<>
        {!['error', 'error-invalid-access-token', 'mapInitialized'].includes(loading) ? (
            <div className="d-flex align-items-center justify-content-center w-100 h-100">
                <LoadingBig show={true} />
            </div>
        ) : ''}

        {loading === 'error' ? (
            <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
                <h1>Oops! Something Went Wrong</h1>
                <p>We encountered an unexpected issue.</p>
                <p>Please let us know what happened by emailing <a href="mailto:team@geolist.co.uk">team@geolist.co.uk</a>. If possible, include details about what you were doing when the error occurred.</p>
            </div>
        ) : ''}

        {loading === 'error-invalid-access-token' ? (
            <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
                <h1>Invalid Share Link</h1>
                <p>The sharing link you used is no longer valid.</p>
                <p>It’s possible that access has been removed. If you believe this is a mistake, please contact the owner of the shared project or email us at <a href="mailto:team@geolist.co.uk">team@geolist.co.uk</a> for assistance.</p>
            </div>
        ) : ''}

        {['dataFetched', 'mapInitialized'].includes(loading) ? (<>
            <Map
                project={project}
                untrackedChanges={untrackedChanges}
                canEdit={allowedActionsRef.current.edit}
                canShare={allowedActionsRef.current.share}

                onProjectChange={onProjectChange}
                onProjectCopy={onProjectCopy}
                onMapInitialized={onMapInitialized}
            />
        </>) : ''}
    </>);
}
